import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/useDebounce';
import {
  useInactiveSensors,
  useFleetSummary,
} from '../hooks/useInactiveSensors';
import { InactiveSensorsSummary } from '../components/InactiveSensorsSummary';
import {
  InactiveSensorsTable,
  COLUMNS,
  loadColumnVisibility,
  saveColumnVisibility,
} from '../components/InactiveSensorsTable';
import { SensorDetailSheet } from '../components/SensorDetailSheet';
import { exportToCSV, copyToClipboard } from '../utils/csvExport';
import {
  INACTIVE_STATUS_CODES,
  type InactiveSensorsFilters as Filters,
  type InactiveSensorStatus,
} from '../types';

// =============================================================================
// localStorage Keys
// =============================================================================

const FILTER_STORAGE_KEY = 'atek-inactive-sensors-column-filters';
const VALID_STATUS_FILTER_VALUES = new Set(INACTIVE_STATUS_CODES.map(String));

function parseStatusFilter(value?: string): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter((status): status is string =>
          typeof status === 'string' && VALID_STATUS_FILTER_VALUES.has(status)
        )
      : [];
  } catch {
    return [];
  }
}

// =============================================================================
// Page Component
// =============================================================================

export default function InactiveSensorsPage() {
  // ---------------------------------------------------------------------------
  // Column filter state (persisted to localStorage)
  // ---------------------------------------------------------------------------
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    () => {
      try {
        return JSON.parse(
          localStorage.getItem(FILTER_STORAGE_KEY) || '{}'
        );
      } catch {
        return {};
      }
    }
  );

  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  // ---------------------------------------------------------------------------
  // Column visibility state (persisted to localStorage)
  // ---------------------------------------------------------------------------
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(loadColumnVisibility);

  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  // ---------------------------------------------------------------------------
  // Sort state
  // ---------------------------------------------------------------------------
  const [sortKey, setSortKey] = useState<string>('timestamp');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection((d) => (d === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDirection(
        key === 'timestamp' || key === 'daysInactive' ? -1 : 1
      );
    }
  };

  // Map sort key for API
  const apiSortKey = sortKey === 'daysInactive' ? 'timestamp' : sortKey;
  const apiSortDirection: 1 | -1 =
    sortKey === 'daysInactive'
      ? sortDirection === 1
        ? -1
        : 1
      : sortDirection;

  // ---------------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------------
  const [page, setPage] = useState(0);
  const pageLength = 50;

  // ---------------------------------------------------------------------------
  // Extract server-side search from name column filter
  // ---------------------------------------------------------------------------
  const nameFilter = columnFilters['name'] || '';
  const debouncedSearch = useDebounce(nameFilter, 300);

  // Extract server-side status filter (single selection → API param)
  const statusFilter = useMemo(() => {
    const selected = parseStatusFilter(columnFilters['status']);
    if (selected.length === 1) {
      return parseInt(selected[0], 10) as InactiveSensorStatus;
    }
    return undefined;
  }, [columnFilters]);

  // Reset page when server-side params change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter, sortKey, sortDirection]);

  // ---------------------------------------------------------------------------
  // Sensor detail sheet
  // ---------------------------------------------------------------------------
  const [selectedSensorId, setSelectedSensorId] = useState<string | null>(
    null
  );

  // ---------------------------------------------------------------------------
  // API query
  // ---------------------------------------------------------------------------
  const filters: Filters = {
    search: debouncedSearch || undefined,
    status: statusFilter,
    page_number: page,
    page_length: pageLength,
    sort_key: apiSortKey,
    sort_direction: apiSortDirection,
  };

  const sensorsQuery = useInactiveSensors(filters);
  const summaryQuery = useFleetSummary();

  const sensors = sensorsQuery.data?.sensors || [];
  const total = sensorsQuery.data?.total || 0;
  const totalPages = Math.ceil(total / pageLength);

  // ---------------------------------------------------------------------------
  // Client-side column filtering (skip server-side columns: name, status)
  // ---------------------------------------------------------------------------
  const filteredSensors = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(
      ([key, v]) => v && key !== 'name' && key !== 'status'
    );
    if (activeFilters.length === 0) return sensors;

    return sensors.filter((sensor) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(sensor);

        // Date range: "from>>to"
        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const dateStr = String(rawValue || '');
          if (from && dateStr < from) return false;
          if (to && dateStr > to + 'T23:59:59') return false;
          return true;
        }

        // Multi-select: JSON array
        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return (
              selected.length === 0 ||
              selected.includes(String(rawValue || ''))
            );
          } catch {
            return true;
          }
        }

        // Number range: "min>>max"
        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (isNaN(num)) return false;
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        // Text: case-insensitive includes
        return String(rawValue ?? '')
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      })
    );
  }, [sensors, columnFilters]);

  // Ignore stale persisted status filters for statuses that are no longer inactive.
  const displaySensors = useMemo(() => {
    const selected = parseStatusFilter(columnFilters['status']);
    if (selected.length > 1) {
      return filteredSensors.filter((s) =>
        selected.includes(String(s.status))
      );
    }
    return filteredSensors;
  }, [filteredSensors, columnFilters]);

  // ---------------------------------------------------------------------------
  // Export handlers
  // ---------------------------------------------------------------------------
  const handleExportCSV = () => {
    exportToCSV(displaySensors, COLUMNS, 'inactive-sensors');
    toast.success(`Exported ${displaySensors.length} rows`);
  };

  const handleCopyClipboard = () => {
    copyToClipboard(displaySensors, COLUMNS);
    toast.success('Copied to clipboard');
  };

  // ---------------------------------------------------------------------------
  // Refresh
  // ---------------------------------------------------------------------------
  const handleRefresh = () => {
    sensorsQuery.refetch();
    summaryQuery.refetch();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            Inactive Sensors{' '}
            <small>Sensors requiring attention</small>
          </h1>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={sensorsQuery.isFetching}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${sensorsQuery.isFetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <InactiveSensorsSummary
        summary={summaryQuery.data}
        isLoading={summaryQuery.isLoading}
      />

      {/* Table (includes filters, sort, pagination, export, column toggle) */}
      {sensorsQuery.error ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-red-500">
              Error loading sensors. Please try again.
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={handleRefresh}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <InactiveSensorsTable
          sensors={displaySensors}
          isLoading={sensorsQuery.isLoading}
          total={total}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          onSensorClick={setSelectedSensorId}
          columnFilters={columnFilters}
          setColumnFilter={setColumnFilter}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onExportCSV={handleExportCSV}
          onCopyClipboard={handleCopyClipboard}
        />
      )}

      {/* Sensor Detail Sheet */}
      <SensorDetailSheet
        sensorId={selectedSensorId}
        open={!!selectedSensorId}
        onOpenChange={(open) => {
          if (!open) setSelectedSensorId(null);
        }}
      />
    </div>
  );
}
